<template>
  <loader v-if="fetchResumeItemPending"/>
  <v-container v-else>
    <v-row>
      <v-col :cols="12">
        <div class="d-flex align-center">
          <v-btn text icon @click="() => $router.go(-1)">
            <v-icon>chevron_left</v-icon>
          </v-btn>
          <h1>
            {{ form.id > 0 ? $t('texts.editEducation') : $t('texts.addEducation') }}
          </h1>
        </div>
      </v-col>
    </v-row>
    <v-row>
      <v-col>
        <v-card>
          <v-card-text>
            <v-form>
              <v-container grid-list-xl class="pa-0">
                <v-row>
                  <v-col :cols="12">
                    <v-text-field
                      :label="$t('texts.title')"
                      v-model="form.title"
                      required
                      :error-messages="errors.collect('title')"
                      v-validate="'required'"
                      data-vv-name="title"
                      :data-vv-as="$t('texts.title')"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col :cols="12">
                    <v-text-field
                      :label="$t('texts.trainingFacility')"
                      v-model="form.organization"
                      required
                      :error-messages="errors.collect('trainingFacility')"
                      v-validate="'required'"
                      data-vv-name="trainingFacility"
                      :data-vv-as="$t('texts.trainingFacility')"
                    />
                  </v-col>
                  <v-col :cols="12" :sm="4">
                    <v-text-field
                      :label="$t('texts.zip')"
                      v-model="form.zip"
                      :error-messages="errors.collect('zip')"
                      v-validate="'min:4|max:5'"
                      data-vv-name="zip"
                      :data-vv-as="$t('texts.zip')"
                    />
                  </v-col>
                  <v-col :cols="12" :sm="8">
                    <v-text-field
                      :label="$t('texts.place')"
                      v-model="form.place"
                      required
                      :error-messages="errors.collect('place')"
                      v-validate="'required'"
                      data-vv-name="place"
                      :data-vv-as="$t('texts.place')"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col :cols="12" :sm="4">
                    <v-menu
                      ref="startDate"
                      :close-on-content-click="false"
                      v-model="startDatePicker"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                      max-width="290px"
                      :return-value.sync="form.start_date"
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          v-on="on"
                          :label="$t('texts.startOfTraining')"
                          :value="form.start_date | formatDateMonth"
                          readonly
                          required
                          :error-messages="errors.collect('startOfTraining')"
                        />
                      </template>
                      <v-date-picker
                        v-model="form.start_date"
                        @input="$refs.startDate.save(form.start_date); setDefaultEndDate()"
                        header-color="primary"
                        color="primary"
                        locale="de-ch"
                        :first-day-of-week="1"
                        type="month"
                        :min="startDatePickerMin"
                        :max="startDatePickerMax"
                        ref="startDatePicker"
                      ></v-date-picker>
                    </v-menu>
                    <input
                      hidden
                      v-model="form.start_date"
                      ref="startDateInput"
                      v-validate="'required|date_format:yyyy-MM'"
                      data-vv-name="startOfTraining"
                      :data-vv-as="$t('texts.startOfTraining')"
                    />
                  </v-col>
                  <v-col :cols="12" :sm="4">
                    <v-menu
                      ref="endDate"
                      :close-on-content-click="false"
                      v-model="endDatePicker"
                      transition="scale-transition"
                      offset-y
                      min-width="290px"
                      max-width="290px"
                      :return-value.sync="form.end_date"
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          v-on="on"
                          :label="$t('texts.endOfTraining')"
                          :value="form.end_date | formatDateMonth"
                          readonly
                          required
                          :error-messages="errors.collect('endOfTraining')"
                        />
                      </template>
                      <v-date-picker
                        v-model="form.end_date"
                        @input="$refs.endDate.save(form.end_date)"
                        header-color="primary"
                        color="primary"
                        locale="de-ch"
                        :first-day-of-week="1"
                        type="month"
                        :min="endDatePickerMin"
                        :max="endDatePickerMax"
                        ref="endDatePicker"
                      ></v-date-picker>
                    </v-menu>
                    <input
                      hidden
                      v-model="form.end_date"
                      v-validate="'required|date_format:yyyy-MM|after:startDateInput'"
                      data-vv-name="endOfTraining"
                      :data-vv-as="$t('texts.endOfTraining')"
                    />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col :cols="12">
                    <v-textarea
                      :label="$t('texts.description')"
                      v-model="form.description"
                    />
                  </v-col>
                </v-row>
                <v-divider/>
                <v-row>
                  <v-col :cols="12">
                    <file-upload
                      :documents="form.documents"
                      @interface="editedDocuments"
                      :uploadType="`educationsUpload-${form.id}`"
                    />
                  </v-col>
                </v-row>
              </v-container>
            </v-form>
          </v-card-text>
          <v-divider/>
          <v-card-actions class="py-3 px-5">
            <v-btn
              color="red darken-2"
              v-if="form.id > 0"
              class="white--text"
              @click="deleteItem()"
              :disabled="storeResumeItemPending || deleteResumeItemPending"
              :loading="deleteResumeItemPending"
            >
              {{ $t('texts.deleteEntry') }}
              <v-icon right class="white--text">delete</v-icon>
            </v-btn>
            <v-spacer/>
            <v-btn
              color="primary" class="white--text"
              @click="handleFormSubmit()"
              :disabled="storeResumeItemPending || deleteResumeItemPending"
              :loading="storeResumeItemPending"
            >
              {{ form.id > 0 ? $t('texts.saveChanges') : $t('texts.save') }}
              <v-icon right class="white--text">check_circle</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <confirmation-dialog ref="confirm"/>
  </v-container>
</template>

<script>
import { createNamespacedHelpers } from 'vuex';
import moment from 'moment';
import FileUpload from '@/components/_organisms/FileUpload.vue';
import ConfirmationDialog from '@/components/_organisms/ConfirmationDialog.vue';

const { mapState: mapEmployeeState, mapActions: mapEmployeeActions } = createNamespacedHelpers('employee');
const { mapActions: mapMessageActions } = createNamespacedHelpers('messages');

export default {
  name: 'EditResumeEducation',
  $_veeValidate: {
    validator: 'new',
  },
  components: {
    FileUpload,
    ConfirmationDialog,
  },
  data() {
    return {
      form: {
        id: null,
        organization: '',
        zip: '',
        place: '',
        start_date: '',
        end_date: '',
        description: '',
        documents: [],
      },
      documents: [],
      startDatePicker: false,
      endDatePicker: false,
    };
  },
  created() {
    this.reset();
    if (this.$route.params.item) {
      Object.assign(this.form, this.$route.params.item);
    } else if (this.$route.params.id) {
      const payload = {
        type: this.$route.meta.resumeType,
        id: this.$route.params.id,
      };
      this.fetchResumeItem(payload).then((response) => {
        Object.assign(this.form, response.data.data);
      });
    }
  },
  computed: {
    ...mapEmployeeState([
      'fetchResumeItemPending',
      'storeResumeItemPending',
      'deleteResumeItemPending',
    ]),
    startDatePickerMin() {
      return moment().subtract(80, 'years').format('YYYY-MM');
    },
    startDatePickerMax() {
      return moment().add(3, 'years').format('YYYY-MM');
    },
    endDatePickerMin() {
      if (this.form.start_date !== null) {
        return this.form.start_date;
      }
      return moment().format('YYYY-MM');
    },
    endDatePickerMax() {
      return moment().add(10, 'years').format('YYYY-MM');
    },
  },
  methods: {
    ...mapEmployeeActions([
      'fetchResumeItem',
      'storeResumeItem',
      'deleteResumeItem',
    ]),
    ...mapMessageActions([
      'showMessageValidateError',
    ]),
    reset() {
      this.form = {
        id: null,
        organization: '',
        zip: '',
        place: '',
        start_date: '',
        end_date: '',
        description: '',
        documents: [],
      };
      this.$validator.reset();
    },
    editedDocuments(editedDocuments) {
      this.form.documents = editedDocuments;
    },
    setDefaultEndDate() {
      if (this.form.end_date === '') {
        const endDate = moment(this.form.start_date).add(1, 'M').format('YYYY-MM');
        this.form.end_date = endDate;
        this.endDatePicker = true;
      }
    },
    handleFormSubmit() {
      this.$validator.validateAll().then((result) => {
        if (result) {
          const payload = {
            type: 'educations',
            item: this.form,
            successMessageKey: this.form.id ? 'alert.updateResumeEducationsSuccessful' : 'alert.addResumeEducationsSuccessful',
            errorMessageKey: this.form.id ? 'alert.updateResumeEducationsError' : 'alert.addResumeEducationsError',
          };
          this.storeResumeItem(payload)
            .then(() => {
              this.$router.go(-1);
              this.$eventBus.$emit('clear-file-upload');
            });
        } else {
          this.showMessageValidateError();
        }
      });
    },
    async deleteItem() {
      const confirmDialog = async () => this.$refs.confirm.open({
        title: 'deleteEntry',
        message: 'deleteEntryQuestion',
        confirm: 'delete',
        reject: 'cancel',
      });
      if (await confirmDialog()) {
        const payload = {
          type: 'educations',
          item: this.form,
          successMessageKey: 'alert.deleteResumeEducationsSuccessful',
          errorMessageKey: 'alert.deleteResumeEducationsError',
        };
        this.deleteResumeItem(payload)
          .then(() => {
            this.$router.go(-1);
          });
      }
    },
  },
  watch: {
    startPicker(value) {
      if (value) {
        this.$nextTick((el) => {
          this.$refs.startDatePicker.activePicker = 'YEAR';
          return el;
        });
      }
    },
    endPicker(value) {
      if (value) {
        this.$nextTick((el) => {
          this.$refs.endDatePicker.activePicker = 'YEAR';
          return el;
        });
      }
    },
  },
};
</script>
